import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import _ from 'lodash';
import { quickScore } from 'quick-score';
import { useCallback, useMemo, useState } from 'react';
import { TGiftType } from '../../entities/gifts/queries';
import { TManagerAccount } from '../../entities/users/queries';
import { TGiftTypes } from '../../generated/graphql';

export const generateMonthFilter = (d: Date | string) => {
  return readableDate(d, 'yyyy-MM');
};

type TUseGiftsFiltersProps = {
  gifts: TGiftType[];
};

export const useGiftsFilters = (props: TUseGiftsFiltersProps) => {
  const { gifts } = props;

  const [monthFilter, setMonthFilter] = useState<string>();
  const [companyFilter, setCompanyFilter] = useState<string>();
  const [officeFilter, setOfficeFilter] = useState<string>();
  const [recipientNameFilter, setRecipientNameFilter] = useState<string>();
  //const [orderByFilter, setOrderByFilter] = useState<string>();
  const [clientManagerFilter, setClientManagerFilter] = useState<string>();
  const [eventTypeFilter, setEventTypeFilter] = useState<TGiftTypes>();

  const companies = useMemo(() => {
    return _.uniqBy(_.compact(gifts.map((e) => e.company)), 'id');
  }, [gifts]);

  const filterByCompany = useCallback(
    (e: TGiftType) => {
      if (companyFilter && e.companyId !== companyFilter) return false;
      return true;
    },
    [companyFilter]
  );

  const companiesCounts = useMemo(
    () =>
      gifts.reduce<Record<string, number>>((acc, e) => {
        const { companyId = '0' } = e;
        acc[companyId] = (acc[companyId] || 0) + 1;
        return acc;
      }, {}),
    [gifts]
  );

  const months = useMemo(() => {
    return _.uniq(
      _.compact(
        gifts.map((e) => {
          return generateMonthFilter(e.eventDate);
        })
      )
    );
  }, [gifts]);

  const filterByMonth = useCallback(
    (e: TGiftType) => {
      if (monthFilter && generateMonthFilter(e.eventDate) !== monthFilter) return false;
      return true;
    },
    [monthFilter]
  );

  const offices = useMemo(() => {
    return _.uniq(_.compact(gifts.filter((e) => filterByCompany(e)).map((e) => e.employee?.office)));
  }, [gifts, filterByCompany]);

  const filterByOffice = useCallback(
    (e: TGiftType) => {
      if (officeFilter && e.employee?.office?.id != officeFilter) return false;
      return true;
    },
    [officeFilter]
  );

  /* const filterOrderBy = useCallback(
    (e: TGiftType[]) => {
      switch (orderByFilter) {
        case 'date_asc':
          return _.orderBy(e, ['eventDate'], ['asc']);
        case 'date_desc':
          return _.orderBy(e, ['eventDate'], ['desc']);
        case undefined:
        default:
          return e;
      }
    },
    [orderByFilter],
  ); */

  const filterByRecipientName = useCallback(
    (e: TGiftType) => {
      if (recipientNameFilter != null && recipientNameFilter != '' && (e.recipient?.fullName == null || quickScore(e.recipient?.fullName, recipientNameFilter) < 0.7)) return false;

      return true;
    },
    [recipientNameFilter]
  );

  const filterByEventType = useCallback(
    (e: TGiftType) => {
      if (eventTypeFilter && e.type != eventTypeFilter) return false;
      return true;
    },
    [eventTypeFilter]
  );

  const clientManagers = useMemo(() => {
    return _.unionBy(
      gifts.reduce<TManagerAccount[]>((acc, e) => {
        const manager = e.company?.accountManager;
        if (manager && typeof manager === 'string') acc.push({ name: manager, id: manager });

        return acc;
      }, []),
      'id'
    );
  }, [gifts]);

  const filterByClientManager = useCallback(
    (e: TGiftType) => {
      if (clientManagerFilter && clientManagerFilter != e.company?.accountManager) return false;

      return true;
    },
    [clientManagerFilter]
  );

  const setCompany = useCallback(
    (companyId: any) => {
      setOfficeFilter(undefined);
      setCompanyFilter(companyId ? String(companyId) : undefined);
    },
    [setCompanyFilter]
  );

  const setMonth = useCallback(
    (month: any) => {
      setOfficeFilter(undefined);
      setMonthFilter(month ? String(month) : undefined);
    },
    [setMonthFilter]
  );

  const setOffice = useCallback((officeId: any) => {
    setOfficeFilter(officeId ? String(officeId) : undefined);
  }, []);

  return {
    companies,
    companiesCounts,
    companyFilter,
    setCompanyFilter,
    filterByCompany,
    setCompany,
    months,
    monthFilter,
    setMonthFilter,
    filterByMonth,
    setMonth,
    offices,
    officeFilter,
    setOfficeFilter,
    filterByOffice,
    setOffice,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    filterByRecipientName,
    /* orderByFilter,
    setOrderByFilter,
    filterOrderBy, */
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter,
    filterByClientManager
  };
};
